/**
 * Run Photo List button.
 *
 * Reads file status from PhotoListContext to determine if vendor files
 * are present and fresh. Disabled with tooltip when files are missing/stale.
 * Shows spinner while running, inline success/error feedback after.
 */
import React, { useState } from "react"

import { MOBILE_FULL_BUTTON } from "../../config/mobileStyles"
import { usePhotoList } from "../../context/PhotoListContext"
import { THEME } from "../../config/theme"
import type { PhotoListRun } from "../../api/photoList"

type Props = {
  /** Receives the finished run so a navigating consumer can carry the
   * "generated (N vehicles)" confirmation to the destination page. */
  onRunComplete?: (run: PhotoListRun) => void
  /** Fill the row (mobile header). The button keeps MOBILE_FULL_BUTTON's
   * height so it lines up with the Print/Export buttons below it. */
  fullWidth?: boolean
  /** True while the upload panel is still pushing a dropped batch to the
   * server. The gate only counts the REQUIRED vendors, so without this the
   * button unlocks the moment the last required file lands — while Dealer
   * Daily / Dealer Connect are still in flight behind the collapsed panel —
   * and an early click silently generates a list with no STOP flags or
   * transit colors. */
  uploadsInFlight?: boolean
}

export function RunPhotoListButton({
  onRunComplete,
  fullWidth = false,
  uploadsInFlight = false,
}: Props) {
  const { fileStatus, fileStatusLoading, isRunning, runPhotoList } = usePhotoList()
  const [result, setResult] = useState<
    | { kind: "idle" }
    | { kind: "success"; message: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" })

  const canRun = fileStatus?.can_run ?? false
  const disabled = isRunning || uploadsInFlight || fileStatusLoading || !canRun

  // Build tooltip for disabled state
  const buildTooltip = (): string | undefined => {
    if (isRunning) return "Run in progress..."
    if (uploadsInFlight) return "Uploads still finishing..."
    if (fileStatusLoading) return "Checking file status..."
    if (!fileStatus) return "File status unavailable"
    if (fileStatus.can_run) return undefined

    const lines: string[] = ["Cannot run Photo List:"]
    const files = fileStatus.files
    for (const [key, entry] of Object.entries(files)) {
      if (!entry.required) continue
      if (entry.status === "missing") {
        lines.push(`  - ${entry.label}: missing`)
      } else if (entry.status === "stale") {
        if (key === "stargard" && entry.stale_count) {
          lines.push(`  - Stargard files stale (${entry.stale_count} of 6 files >1hr old)`)
        } else {
          lines.push(`  - ${entry.label}: stale`)
        }
      }
    }
    if (fileStatus.stargard_missing.length > 0) {
      lines.push(
        `  - Stargard missing: ${fileStatus.stargard_missing.join(", ")}`,
      )
    }
    return lines.join("\n")
  }

  const handleClick = async () => {
    setResult({ kind: "idle" })
    try {
      const run = await runPhotoList()
      setResult({
        kind: "success",
        message: `Photo list generated (${run.total_count} vehicles)`,
      })
      onRunComplete?.(run)
      // Clear success message after 5 seconds
      setTimeout(() => setResult({ kind: "idle" }), 5000)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Run failed"
      setResult({ kind: "error", message })
      setTimeout(() => setResult({ kind: "idle" }), 8000)
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: fullWidth ? "stretch" : "flex-end",
        gap: 4,
        ...(fullWidth && { width: "100%" }),
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={buildTooltip()}
        style={{
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          background: THEME.colors.navy,
          color: THEME.colors.white,
          fontWeight: 500,
          fontSize: 14,
          border: "none",
          borderRadius: THEME.borderRadius.md,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: THEME.fonts.primary,
          whiteSpace: "nowrap",
          transition: "opacity 0.15s",
          // flex: initial — the shared override's flex:1 targets siblings in a
          // button row; here the button is alone in a column and just fills it.
          ...(fullWidth && { ...MOBILE_FULL_BUTTON, flex: "initial", width: "100%" }),
        }}
      >
        {isRunning ? "Generating..." : "Generate Photo List"}
      </button>

      {result.kind === "success" && (
        <div
          style={{
            fontSize: 12,
            color: THEME.colors.semantic.success,
            fontWeight: 500,
          }}
        >
          {result.message}
        </div>
      )}
      {result.kind === "error" && (
        <div
          style={{
            fontSize: 12,
            color: THEME.colors.semantic.danger,
            fontWeight: 500,
            maxWidth: 250,
          }}
        >
          {result.message}
        </div>
      )}
    </div>
  )
}
